import { useCallback, useEffect, useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';
export type SortKind = 'number' | 'text' | 'date';
type SortValue = number | string | Date | null | undefined;

export interface SortColumn<T> {
  value: (row: T) => SortValue;
  kind?: SortKind;
  /** Lower-case phrase for the "Sorted by …" line, e.g. "Medicare NCR". */
  label: string;
}

export interface SortedPagedOptions<T, K extends string> {
  rows: readonly T[];
  columns: Record<K, SortColumn<T>>;
  defaultSort: { key: K; dir: SortDir };
  /** Omit for an unpaged list. */
  pageSize?: number;
  /** Changing this (e.g. a filter or category) returns to page 1. */
  resetKey?: unknown;
}

export interface SortedPaged<T, K extends string> {
  pageRows: T[];
  sorted: T[];
  sortKey: K;
  sortDir: SortDir;
  toggleSort: (key: K) => void;
  page: number;
  setPage: (page: number) => void;
  pageCount: number;
  total: number;
  /** 1-based inclusive range of the current page; 0–0 when empty. */
  rangeStart: number;
  rangeEnd: number;
  sortLine: string;
}

const toComparable = (value: SortValue, kind: SortKind): number | string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (kind === 'text') return String(value);
  if (kind === 'date') { const t = value instanceof Date ? value.getTime() : new Date(value).getTime(); return Number.isNaN(t) ? null : t; }
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

export const describeSort = (label: string, kind: SortKind, dir: SortDir) => {
  const how = kind === 'text' ? (dir === 'asc' ? 'A–Z' : 'Z–A') : kind === 'date' ? (dir === 'desc' ? 'newest first' : 'oldest first') : (dir === 'desc' ? 'high to low' : 'low to high');
  return `Sorted by ${label}, ${how}`;
};

/**
 * Sorts the full set, then paginates, so every page reflects one global order.
 * Pinned rows (Current, Proposed, …) are the caller's: render them above `pageRows` and keep them out of `rows`.
 * First click on a column sorts descending (text columns ascending); clicking the active column flips it.
 * Any sort change returns to page 1. Empty values always sort last.
 */
export function useSortedPaged<T, K extends string>({ rows, columns, defaultSort, pageSize, resetKey }: SortedPagedOptions<T, K>): SortedPaged<T, K> {
  const [sortKey, setSortKey] = useState<K>(defaultSort.key);
  const [sortDir, setSortDir] = useState<SortDir>(defaultSort.dir);
  const [page, setPageState] = useState(0);

  useEffect(() => { setPageState(0); }, [resetKey]);

  const sorted = useMemo(() => {
    const column = columns[sortKey];
    const kind = column.kind ?? 'number';
    const sign = sortDir === 'asc' ? 1 : -1;
    return rows
      .map((row, index) => ({ row, index, v: toComparable(column.value(row), kind) }))
      .sort((a, b) => {
        if (a.v === null && b.v === null) return a.index - b.index;
        if (a.v === null) return 1;
        if (b.v === null) return -1;
        const c = typeof a.v === 'string' ? a.v.localeCompare(b.v as string, undefined, { numeric: true, sensitivity: 'base' }) : (a.v as number) - (b.v as number);
        return c !== 0 ? c * sign : a.index - b.index;
      })
      .map((entry) => entry.row);
  }, [rows, columns, sortKey, sortDir]);

  const total = sorted.length;
  const size = pageSize && pageSize > 0 ? pageSize : Math.max(total, 1);
  const pageCount = Math.max(1, Math.ceil(total / size));
  const current = Math.min(page, pageCount - 1);
  const pageRows = useMemo(() => sorted.slice(current * size, current * size + size), [sorted, current, size]);

  const toggleSort = useCallback((key: K) => {
    if (key === sortKey) setSortDir((dir) => (dir === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir((columns[key].kind ?? 'number') === 'text' ? 'asc' : 'desc'); }
    setPageState(0);
  }, [sortKey, columns]);

  const setPage = useCallback((next: number) => setPageState(Math.max(0, Math.min(next, pageCount - 1))), [pageCount]);

  return {
    pageRows, sorted, sortKey, sortDir, toggleSort, page: current, setPage, pageCount, total,
    rangeStart: total ? current * size + 1 : 0,
    rangeEnd: Math.min(total, (current + 1) * size),
    sortLine: describeSort(columns[sortKey].label, columns[sortKey].kind ?? 'number', sortDir),
  };
}
